import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { useEffect } from 'react';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { TileCarousel } from '../TileCarousel/index.tsx';
import { formatCredits } from './helpers/formatCredits.ts';
import { type Credit, type CreditsResponse, type Props } from './types.ts';

const queries = {
  [ScreenType.Movie]: "\nquery GetPersonMovieCredits(\n  $id: ID!\n) {\n  person(id: $id) {\n    ...PersonMovieCredits\n  }\n}\n\nfragment PersonMovieCredits on Person {\n  movieCredits {\n    cast {\n      __typename\n      character\n      posterPath\n      title\n      voteAverage\n      voteCount\n    }\n    crew {\n      __typename\n      job\n      posterPath\n      title\n      voteAverage\n      voteCount\n    }\n  }\n}\n",
  [ScreenType.Tv]: "\nquery GetPersonTvCredits(\n  $id: ID!\n) {\n  person(id: $id) {\n    ...PersonTvCredits\n  }\n}\n\nfragment PersonTvCredits on Person {\n  tvCredits {\n    cast {\n      __typename\n      character\n      posterPath\n      name\n      voteAverage\n      voteCount\n    }\n    crew {\n      __typename\n      job\n      posterPath\n      name\n      voteAverage\n      voteCount\n    }\n  }\n}\n",
};

export const PersonCreditsCarousel = <C extends Credit>({
  children,
  filter,
  id,
  setActivePreview,
  subType,
  type,
}: Props<C>) => {
  const query = queries[type];
  const { config } = useToolbelt();

  const { execute, loading, result } = useQuery<CreditsResponse>(query, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { id } }, { data: { initiator: 'PersonCreditsCarousel' } });
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const items = formatCredits(result?.data, type, subType, config, filter);

  if (!loading && items.length === 0) {
    return;
  }

  const tileCarousel = <TileCarousel items={items} setActivePreview={setActivePreview} />;
  return children ? children(tileCarousel) : tileCarousel;
};
